import React, { useCallback, useState, useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

import { isNaN } from 'lodash';
import Tooltip from '../../../../../../components/Tooltip';

const PaymentsTable = ({ headers, items, currencyTemplate, values, onChangeCheck, onSortTable }) => {
  const [sort, setSort] = useState({
    code: 'amount',
    order: 'desc',
  });

  const getSortIcon = useCallback(
    (head) => {
      if (sort.code === head.code && sort.order === 'desc') {
        return faSortDown;
      }
      if (sort.code === head.code && sort.order === 'asc') {
        return faSortUp;
      }
      if (sort.code !== head.code) {
        return faSort;
      }
      return null;
    },
    [sort],
  );

  const totalAmount = useMemo(() => {
    const sum = items.filter((item) => values.includes(item.title)).reduce((acc, item) => (acc += Number(item?.amount)), 0);
    const fixedSum = sum?.toFixed(2) || sum;
    return currencyTemplate?.replace('{{value}}', fixedSum) || fixedSum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, values]);
  const totalCount = useMemo(() => {
    const sum = items.filter((item) => values.includes(item.title)).reduce((acc, item) => (acc += Number(item?.count)), 0);
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, values]);

  const computedAverage = useCallback(
    () => {
      const amounts = items?.filter((item) => values.includes(item.title)).reduce((acc, item) => (acc += Number(item?.amount)), 0);
      const counts = items?.filter((item) => values.includes(item.title)).reduce((acc, item) => (acc += Number(item?.count)), 0);
      const sum = amounts / counts;
      if (isNaN(sum)) {
        return currencyTemplate?.replace('{{value}}', '0.00')
      }
      return currencyTemplate?.replace('{{value}}', sum?.toFixed(2)) || sum;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, values],
  );

  const onSort = (event, head) => {
    event?.preventDefault();
    const order = head.code !== sort.code ? 'desc' : sort.order === 'asc' ? 'desc' : 'asc';

    setSort({
      code: head.code,
      order,
    });
    onSortTable?.({ code: head.code, order });
  };

  return (
    <Table responsive className="table-grey chart-payments-table">
      <thead>
        <tr>
          <th className="color-identification">{}</th>
          <th className="all-action">
            <input type="checkbox" onChange={onChangeCheck} checked={values.length === items.length} value="all" name="all" />
          </th>
          {headers?.map((head) => (
            <th key={head.code}>
              {head?.sortable ? (
                <a href="# " onClick={(e) => onSort(e, head)}>
                  <span>{head.title}</span>
                  {' '}
                  <FontAwesomeIcon icon={getSortIcon(head)} />
                </a>
              ) : (
                <span>{head.title}</span>
              )}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {items?.map((item) => {
          const checked = values?.includes(item.title);

          return (
            <tr key={item.title} className={`${checked ? '' : 'disabled'}`}>
              <td className="color-identification">
                {checked && <div className="color-identification__line" style={{ backgroundColor: item.color }} />}
              </td>
              <td className="item-action">
                <input type="checkbox" onChange={onChangeCheck} checked={checked} value={item.title} />
              </td>
              <td className="item-name">
                <Tooltip placement="right" tooltipClass="tooltip__right hide-arrow" text={item.tooltip}>
                  <span>{item.title}</span>
                </Tooltip>
              </td>
              <td className="item-cell">{item.amount_formatted}</td>
              <td className="item-cell">{item.count}</td>
              <td className="item-cell">{item.average_formatted}</td>
            </tr>
          );
        })}
        <tr className="total-row">
          <td className="color-identification">{}</td>
          <td colSpan={2}>Total</td>
          <td>{totalAmount}</td>
          <td>{totalCount}</td>
          <td>{computedAverage()}</td>
        </tr>
      </tbody>
    </Table>
  );
};

PaymentsTable.propTypes = {
  headers: PropTypes.array,
  items: PropTypes.array,
  currencyTemplate: PropTypes.string,
  values: PropTypes.array,
  onChangeCheck: PropTypes.func,
  onSortTable: PropTypes.func,
};

export default PaymentsTable;
